import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Download, ExternalLink, FileText, Printer, QrCode, Share2 } from "lucide-react";
import { FLYERS, FlyerCard } from "./CreditUnion";
import { assetUrl, useUploadedFlyers } from "../lib/flyers";

const SECTION_ORDER = [["credit_union", "Credit Union"], ["fund", "Fund"], ["community", "Community"]];

const CIRCULATE = {
  pdf: "/api/static/marketing/circulate_the_dollar_flyer.pdf?v=1",
  pages: [
    "/api/static/marketing/circulate_the_dollar_p1.jpg?v=1",
    "/api/static/marketing/circulate_the_dollar_p2.jpg?v=1",
  ],
};
const RALLY_FLYER = "/brand/rally-of-the-minds-flyer.jpg";
const CU_NAME = "Proposed Trillion Dollar Spending Power Federal Credit Union";

async function shareLink(path, title, text) {
  const url = `${window.location.origin}${path}`;
  try {
    if (navigator.share) {
      await navigator.share({ title, text, url });
    } else {
      await navigator.clipboard.writeText(`${text}\n\n${url}`);
      toast.success("Link copied to clipboard");
    }
  } catch (_e) {
    /* user cancelled */
  }
}

const btn = "inline-flex items-center gap-2";

function Hero({ uploadedSections }) {
  const jumps = [
    ["#circulate-the-dollar", "Circulate the Dollar"],
    ["#rally", "Rally of the Minds"],
    ["#social", "Social media"],
    ...uploadedSections.map(([key, label]) => [`#uploaded-${key}`, `${label} flyers`]),
  ];
  return (
    <section className="border-b border-bdu-gold/10" data-testid="flyers-hero">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 pt-20 pb-14">
        <div className="label-eyebrow">{CU_NAME} · Flyers</div>
        <h1 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl text-bdu-cream leading-[1.05] max-w-4xl">
          Flyers that move people <span className="text-bdu-gold italic">to the table.</span>
        </h1>
        <p className="mt-6 text-bdu-cream/70 max-w-2xl leading-relaxed">
          Print them for the barbershop, the church foyer and the corner store. Post them on every feed.
          Every scan and share brings the charter closer.
        </p>
        <div className="mt-8 flex flex-wrap gap-3" data-testid="flyers-jump-links">
          {jumps.map(([to, label]) => (
            <Link key={to} to={`/flyers${to}`} className="text-[11px] uppercase tracking-[0.2em] border border-bdu-gold/30 px-4 py-2 text-bdu-cream/80 hover:border-bdu-gold hover:text-bdu-gold transition-colors">
              {label}
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function CirculateFlyer() {
  return (
    <section id="circulate-the-dollar" className="border-b border-bdu-gold/10" data-testid="flyer-circulate-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-20 grid lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5">
          <div className="label-eyebrow">Print flyer · Credit Union</div>
          <h2 className="mt-4 font-serif text-3xl sm:text-4xl text-bdu-cream">Circulate the Dollar</h2>
          <div className="mt-2 text-[11px] uppercase tracking-[0.2em] text-bdu-cream/45">2 pages · 8.5 × 11 in · PDF</div>
          <p className="mt-5 text-bdu-cream/70 leading-relaxed">
            "Own the bank that builds the community." Explains the proposed credit union, the Fund's
            $500,000 anchor sponsorship, and who can join across Fulton and DeKalb counties.
          </p>
          <div className="mt-5 flex items-start gap-3 border border-bdu-gold/20 bg-bdu-surface2 p-4 text-sm text-bdu-cream/75">
            <QrCode size={18} className="text-bdu-gold shrink-0 mt-0.5" />
            <span>The live QR code opens the Credit Union membership pledge form. Scans are counted as flyer visits, so you can see what's working.</span>
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href={CIRCULATE.pdf} download="Circulate_the_Dollar_Flyer.pdf" className={`btn-gold ${btn}`} data-testid="flyer-circulate-download">
              <Download size={16} /> Download PDF
            </a>
            <a href={CIRCULATE.pdf} target="_blank" rel="noopener noreferrer" className={`btn-ghost ${btn}`} data-testid="flyer-circulate-print">
              <Printer size={16} /> Open to print
            </a>
            <button type="button" className={`btn-ghost ${btn}`} data-testid="flyer-circulate-share"
                    onClick={() => shareLink("/flyers#circulate-the-dollar", "Circulate the Dollar flyer",
                      `Own the bank that builds the community: the ${CU_NAME}. Pending NCUA charter approval.`)}>
              <Share2 size={16} /> Share
            </button>
          </div>
          <p className="mt-4 text-xs text-bdu-cream/45">Tip: print double-sided so the QR side faces out.</p>
        </div>
        <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
          {CIRCULATE.pages.map((src, i) => (
            <a key={src} href={src} target="_blank" rel="noopener noreferrer"
               className="group block bg-white ring-1 ring-bdu-gold/25 hover:ring-bdu-gold transition-shadow"
               data-testid={`flyer-circulate-page-${i + 1}`}>
              <img src={src} alt={`Circulate the Dollar flyer, page ${i + 1}`} className="w-full aspect-[612/792] object-cover object-top" loading="lazy" />
              <div className="bg-bdu-surface2 px-3 py-2 text-[10px] uppercase tracking-[0.22em] text-bdu-cream/55 group-hover:text-bdu-gold">
                Page {i + 1} {i === 0 ? "· with QR code" : ""}
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

function RallyFlyer() {
  return (
    <section id="rally" className="border-b border-bdu-gold/10" data-testid="flyer-rally-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-20 grid lg:grid-cols-12 gap-12 items-center">
        <a href={RALLY_FLYER} target="_blank" rel="noopener noreferrer" className="lg:col-span-5 block ring-1 ring-bdu-gold/25 hover:ring-bdu-gold transition-shadow"
           data-testid="flyer-rally-image">
          <img src={RALLY_FLYER} alt="Rally Of The Minds · The Rise II Power Movement, official flyer" className="w-full aspect-[896/1200] object-cover" loading="lazy" />
        </a>
        <div className="lg:col-span-7">
          <div className="label-eyebrow">Event flyer · Community</div>
          <h2 className="mt-4 font-serif text-3xl sm:text-4xl text-bdu-cream">Rally of the Minds</h2>
          <div className="mt-2 text-[11px] uppercase tracking-[0.2em] text-bdu-cream/45">The Rise II Power Movement · JPG</div>
          <p className="mt-5 text-bdu-cream/70 leading-relaxed max-w-xl">
            The official rally flyer. Post it, text it, print it. Then send people to the rally page for the full details and RSVP.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href={RALLY_FLYER} download="Rally_of_the_Minds_Flyer.jpg" className={`btn-gold ${btn}`} data-testid="flyer-rally-download">
              <Download size={16} /> Download flyer
            </a>
            <button type="button" className={`btn-ghost ${btn}`} data-testid="flyer-rally-share"
                    onClick={() => shareLink("/rally", "Rally of the Minds", "Join us at Rally of the Minds, The Rise II Power Movement.")}>
              <Share2 size={16} /> Share
            </button>
            <Link to="/rally" className={`btn-ghost ${btn}`} data-testid="flyer-rally-details">
              <ExternalLink size={16} /> Rally details
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function SocialFlyers() {
  const share = (f) => shareLink("/credit-union", "Proposed Trillion Dollar Spending Power FCU",
    `Learn how you can support the ${CU_NAME}. A non-binding pledge of charter support, not a deposit. Pending NCUA approval.`);
  return (
    <section id="social" className="border-b border-bdu-gold/10" data-testid="flyer-social-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-20">
        <div className="label-eyebrow">Social media · Credit Union</div>
        <h2 className="mt-4 font-serif text-3xl sm:text-4xl text-bdu-cream">Social media flyers</h2>
        <p className="mt-4 text-bdu-cream/70 max-w-2xl">Sized for every feed, each with a live QR code to the Credit Union page.</p>
        <div className="mt-10 grid md:grid-cols-3 gap-8">
          {FLYERS.map((f) => <FlyerCard key={f.key} flyer={f} onShare={() => share(f)} />)}
        </div>
      </div>
    </section>
  );
}

function UploadedCard({ f }) {
  const extra = f.file_type === "pdf" && f.page_count > 1 ? ` · ${f.page_count} pages` : "";
  return (
    <div id={`flyer-${f.id}`} className="group border border-bdu-gold/20 bg-bdu-surface2 p-3 hover:border-bdu-gold/60 transition-colors flex flex-col"
         data-testid={`flyer-uploaded-${f.id}`}>
      <a href={assetUrl(f.file_url)} target="_blank" rel="noopener noreferrer" className="block bg-white/5 ring-1 ring-bdu-gold/20 overflow-hidden">
        {f.preview_urls[0] && (
          <img src={assetUrl(f.preview_urls[0])} alt={`${f.title} flyer`} className="w-full h-auto transition-transform duration-300 group-hover:scale-[1.02]" loading="lazy" />
        )}
      </a>
      <div className="px-1 pt-4 pb-1 flex-1 flex flex-col">
        <div className="text-base font-serif text-bdu-cream leading-tight">{f.title}</div>
        <div className="mt-1 text-[0.65rem] uppercase tracking-widest text-bdu-cream/40 inline-flex items-center gap-1.5">
          <FileText size={11} /> {f.file_type === "pdf" ? "PDF" : "Image"}{extra}
        </div>
        {f.description && <p className="mt-3 text-sm text-bdu-cream/65 leading-relaxed">{f.description}</p>}
        <div className="mt-auto pt-4 flex gap-2">
          <a href={assetUrl(f.download_url)} className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 text-[0.65rem] uppercase tracking-widest bg-bdu-gold text-black font-semibold hover:brightness-110 transition"
             data-testid={`flyer-uploaded-${f.id}-download`}>
            <Download size={13} /> Download
          </a>
          <a href={assetUrl(f.file_url)} target="_blank" rel="noopener noreferrer" title="Open full size" aria-label={`Open ${f.title} full size`}
             className="inline-flex items-center justify-center px-3 py-2 border border-bdu-gold/40 text-bdu-cream hover:bg-bdu-gold/10" data-testid={`flyer-uploaded-${f.id}-open`}>
            <ExternalLink size={13} />
          </a>
          <button type="button" title="Share" aria-label={`Share ${f.title}`} onClick={() => shareLink(f.share_path, f.title, `${f.title} · Black Dollars United`)}
                  className="inline-flex items-center justify-center px-3 py-2 border border-bdu-gold/40 text-bdu-cream hover:bg-bdu-gold/10" data-testid={`flyer-uploaded-${f.id}-share`}>
            <Share2 size={13} />
          </button>
        </div>
      </div>
    </div>
  );
}

function UploadedSections({ flyers, sections }) {
  return sections.map(([key, label]) => (
    <section key={key} id={`uploaded-${key}`} className="border-b border-bdu-gold/10" data-testid={`flyer-uploaded-section-${key}`}>
      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-20">
        <div className="label-eyebrow">More flyers · {label}</div>
        <h2 className="mt-4 font-serif text-3xl sm:text-4xl text-bdu-cream">{label} flyers</h2>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-8 items-start">
          {flyers.filter((f) => f.section === key).map((f) => <UploadedCard key={f.id} f={f} />)}
        </div>
      </div>
    </section>
  ));
}

export default function Flyers() {
  const uploaded = useUploadedFlyers();
  const sections = SECTION_ORDER.filter(([key]) => uploaded.some((f) => f.section === key));
  return (
    <div data-testid="flyers-page">
      <Hero uploadedSections={sections} />
      <CirculateFlyer />
      <RallyFlyer />
      <SocialFlyers />
      <UploadedSections flyers={uploaded} sections={sections} />
      <p className="max-w-7xl mx-auto px-6 sm:px-10 py-10 text-xs text-bdu-cream/45 leading-relaxed" data-testid="flyers-disclaimer">
        The {CU_NAME} is pending NCUA charter approval and is not yet operational. Membership pledges are non-binding
        statements of support. They are not deposits or accounts.
      </p>
    </div>
  );
}
